import React, { useState, useEffect, useRef } from 'react';
import { MdClose, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { getRequestItems } from '../../../api/requests';
import { mediaUrl } from '../../../api/client';
import noImageIcon from '../../../images/no-image-icon.svg';
import styles from './RequestItemsModal.module.css';
import dialog from '../../../styles/shared/Dialog.module.css';

const NO_DISPONIBLE = 'No Disponible';

const RequestItemsModal = ({ onClose, pedido, handleShowCheckList, escapeDisabled = false }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const modalRef = useRef();
    const closeButtonRef = useRef(null);

    useEffect(() => {
        const fetchItems = async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const response = await getRequestItems(pedido.id);
                setItems(response.data);
            } catch (error) {
                console.error('Error fetching items:', error);
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, [pedido]);

    useEffect(() => {
        closeButtonRef.current?.focus();
    }, []);

    useEffect(() => {
        if (escapeDisabled) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [escapeDisabled, onClose]);

    const getImageUrl = (imagePath) => mediaUrl(imagePath) || noImageIcon;

    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            onClose();
        }
    };

    const countLabel = `${items.length} ${items.length === 1 ? 'ítem' : 'ítems'}`;

    return (
        <div className={styles.overlay} onClick={handleClickOutside}>
            <div
                className={styles.container}
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="request-items-title"
            >
                <div className={styles.header}>
                    <div>
                        <h2 id="request-items-title" className={styles.title}>Ítems de la solicitud</h2>
                        {!loading && !loadError && <p className={styles.subtitle}>{countLabel}</p>}
                    </div>
                    <button
                        type="button"
                        ref={closeButtonRef}
                        className={styles.close}
                        onClick={onClose}
                        aria-label="Cerrar"
                    >
                        <MdClose aria-hidden="true" />
                    </button>
                </div>
                <div className={styles.body}>
                    {loading && <p className={styles.message}>Cargando ítems...</p>}
                    {!loading && loadError && (
                        <p className={`${styles.message} ${styles.messageError}`} role="alert">
                            No se pudieron cargar los ítems. Inténtalo de nuevo.
                        </p>
                    )}
                    {!loading && !loadError && items.length === 0 && (
                        <p className={styles.message}>Esta solicitud no tiene ítems.</p>
                    )}
                    {!loading && items.length > 0 && (
                        <ul className={styles.list}>
                            {items.map(item => {
                                const imageUrl = item.image ? getImageUrl(item.image.image) : noImageIcon;
                                const expanded = expandedItems[item.id] || false;
                                const detailsId = `request-item-details-${item.id}`;
                                const handleExpandClick = () => {
                                    setExpandedItems(prev => ({
                                        ...prev,
                                        [item.id]: !expanded
                                    }));
                                };
                                const details = [
                                    ['Descripción', item.descripcion],
                                    ['Estado', item.estado],
                                    ['Sección', item.seccion],
                                    ['Fecha Vencimiento', item.fechaVencimiento],
                                    ['Observación', item.observacion],
                                    ['Proveedor', item.proveedor],
                                ];
                                return (
                                    <li key={item.id} className={styles.item}>
                                        <div className={styles.summary}>
                                            <img src={imageUrl} alt={item.equipo || ''} className={styles.image} />
                                            <div className={styles.info}>
                                                <p className={styles.name}>{item.equipo ? item.equipo : NO_DISPONIBLE}</p>
                                                <p className={styles.brand}>
                                                    Marca: {item.marca ? item.marca : NO_DISPONIBLE}
                                                </p>
                                            </div>
                                            <span className={styles.quantity} title="Cantidad solicitada">
                                                <b>{item.cantidad}</b> unid.
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.toggle}
                                            onClick={handleExpandClick}
                                            aria-expanded={expanded}
                                            aria-controls={detailsId}
                                        >
                                            {expanded ? 'Ocultar detalles' : 'Ver más detalles'}
                                            {expanded ? <MdExpandLess aria-hidden="true" /> : <MdExpandMore aria-hidden="true" />}
                                        </button>
                                        {expanded && (
                                            <dl id={detailsId} className={styles.details}>
                                                {details.map(([label, value]) => (
                                                    <div key={label} className={styles.detailRow}>
                                                        <dt>{label}</dt>
                                                        <dd>{value ? value : NO_DISPONIBLE}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <div className={styles.footer}>
                    <div className={`${dialog.buttons} ${styles.buttons}`}>
                        <button type="button" className={dialog.cancelButton} onClick={onClose}>Cerrar</button>
                        <button type="button" className={dialog.confirmButton} onClick={() => handleShowCheckList(pedido)}>
                            Mostrar checklist
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestItemsModal;
